// Permission constants for common actions
export const PERMISSION_ACTIONS = {
  LOGIN: 'permission_action_login',
  VIEW: 'permission_action_view',
  CREATE: 'permission_action_create',
  UPDATE: 'permission_action_update',
  DELETE: 'permission_action_delete',
  CHANGE: 'permission_action_change',
  IMPORT: 'permission_action_import',
  EXPORT: 'permission_action_export',
  EDIT: 'permission_action_edit',
} as const;

// Permission groups
export const PERMISSION_GROUPS = {
  USERS: 'permission_group_users',
  SUPPLIERS: 'permission_group_suppliers',
  STAND_TYPES: 'permission_group_stand_types',
  TEAMS: 'permission_group_teams',
  ORGANIZERS: 'permission_group_organizers',
  EQUIPMENTS: 'permission_group_equipments',
  VARIABLE_LISTS: 'permission_group_variable_lists',
  USER_ROLES: 'permission_group_user_roles',
  ROLES: 'permission_group_user_roles',
  EXHIBITION_TYPES: 'permission_group_exhibition_types',
  FLOORINGS: 'permission_group_floorings',
  GRAPHIC_RULES: 'permission_group_graphic_rules',
} as const;

// Entry points
export const ENTRY_POINTS = {
  LIBRARY: 'library',
  APP: 'app',
} as const;

// Helper function to build permission slug
export const buildPermissionSlug = (action: string, group: string): string => {
  return `permission_${action}_${group}`;
};

// Helper function to parse permission slug
export const parsePermissionSlug = (slug: string): { action: string; group: string } | null => {
  const match = slug.match(/^permission_(.+)_(.+)$/);
  if (!match) return null;

  return {
    action: match[1],
    group: match[2],
  };
};

// Helper function to check if permission is library permission
export const isLibraryPermission = (permission: any): boolean => {
  return permission?.entry_point === ENTRY_POINTS.LIBRARY;
};

// Helper function to create permission check parameters
export const createPermissionCheck = (
  slug: string,
  entryPoint: string = ENTRY_POINTS.LIBRARY,
  groupName: string,
  action: string
) => {
  return {
    slug,
    entryPoint,
    groupName,
    action,
  } as const;
};

// Helper function to get permission display name
export const getPermissionDisplayName = (permission: any, lang: string = 'en'): string => {
  if (!permission?.translation) return permission?.slug || 'Unknown Permission';

  const translation = permission.translation.find((t: any) => t.lang === lang);
  return translation?.value || permission.slug || 'Unknown Permission';
};

// Helper function to group permissions by model
export const groupPermissionsByModel = (permissions: any[]): Record<string, any[]> => {
  const groups: Record<string, any[]> = {};

  permissions.forEach(permission => {
    if (!isLibraryPermission(permission)) return;

    const group = permission.group_name || 'other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(permission);
  });

  return groups;
};

// Helper function to check if user has developer role
export const hasDeveloperRole = (roles: any[]): boolean => {
  return roles?.some(role => role.slug === 'role_developer') || false;
};

// Helper function to filter library permissions from user data
export const getLibraryPermissions = (userPermissions: any[], userRoles: any[]): any[] => {
  if (!userPermissions || !userRoles) return [];

  // Get full permission data from roles
  const fullPermissions = userRoles.flatMap(role => role.permissions || []);

  return userPermissions.filter(perm => {
    const fullPermission = fullPermissions.find(p => p.id === perm.id);
    return isLibraryPermission(fullPermission);
  });
};

// Helper function to generate permission constants dynamically
export const generatePermissionConstants = (permissions: any[]) => {
  const constants: Record<string, any> = {};

  permissions.forEach(permission => {
    if (permission.entry_point !== ENTRY_POINTS.LIBRARY) return;

    // Extract action and group from slug
    const parsed = parsePermissionSlug(permission.slug);
    if (!parsed) return;

    const { action, group } = parsed;

    // Create key for constant
    const key = `${action.toUpperCase()}_${group.toUpperCase()}`;

    constants[key] = createPermissionCheck(
      permission.slug,
      permission.entry_point,
      permission.group_name,
      permission.action
    );
  });

  return constants;
};

// Helper function to get permission by slug dynamically
export const getPermissionBySlug = (permissions: any[], slug: string) => {
  const permission = permissions.find(p => p.slug === slug && p.entry_point === ENTRY_POINTS.LIBRARY);

  if (!permission) return null;

  return createPermissionCheck(
    permission.slug,
    permission.entry_point,
    permission.group_name,
    permission.action
  );
};

// Helper function to get all permissions for a specific group
export const getPermissionsByGroup = (permissions: any[], groupName: string) => {
  return permissions
    .filter(p => p.entry_point === ENTRY_POINTS.LIBRARY && p.group_name === groupName)
    .map(permission => createPermissionCheck(
      permission.slug,
      permission.entry_point,
      permission.group_name,
      permission.action
    ));
};